/**
 * ZTP activation fixtures.
 *
 * Same determinism rules as `devices.ts` / `records.ts`: fixed literals, ISO
 * times relative to module load, wire id forms (`job:…`, `acs-device:<serial>`).
 *
 * Why these exist as fixtures at all: the façade derives an activation's
 * advancement at read-time from store state (`acs_service/routers/ops.py`
 * `get_activation`) — `await_inform → informed → bound → provisioned`, `failed`
 * terminal. A console session cannot create that state from the browser, so the
 * preseeded set below stands in for activations that have already advanced, and
 * `createActivation` in `fixtureApi.ts` produces the real `await_inform` answer
 * for anything the operator submits. Every shape here is the `ActivationStatus`
 * contract shape, no console-only fields.
 */

import type { ActivationStatus, ActivationStatusValue, ActivationStep } from '../api/types';
import { isoAgo } from './devices';

/**
 * OLT ids and PON ports the console treats as valid input examples. These are
 * the `olt_ref` values already present in `records.ts` (the ONT/OLT event
 * fixtures) plus the same naming the plan uses — NOT an inventory endpoint:
 * `GET /ops/v1/olts` is still pending (plan page 9, 🔧T10), so the activation
 * form cannot list them from the server yet and says so.
 */
export const FIXTURE_OLT_IDS: readonly string[] = [
  'olt-tehran-01',
  'olt-tehran-02',
  'olt-karaj-01',
  'olt-qom-01',
  'olt-isfahan-02',
  'olt-mashhad-01',
];

export const FIXTURE_PON_PORTS: readonly string[] = ['PON-0/1', 'PON-0/2', 'PON-0/3', 'PON-0/4'];

export const DEFAULT_LINE_PROFILE = 'LP-XGP-30';
export const DEFAULT_SERVICE_PROFILE = 'SP-INET-1G';

/** The TR-069 URL the canned adapter pushes (`ACS_CWMP_URL_FOR_DEVICE` default). */
export const CANNED_CWMP_URL = 'http://127.0.0.1:7547/';
export const CANNED_INFORM_INTERVAL = 300;

/** One `ok` adapter step, shaped exactly like `routers/ops.py` records them. */
export function okStep(step: string, ago: number, result: Record<string, unknown>): ActivationStep {
  return { step, status: 'ok', at: isoAgo(ago), result };
}

/** A step that failed the activation (`status: "failed"` plus the adapter error). */
export function failedStep(
  step: string,
  ago: number,
  code: string,
  detail: string,
): ActivationStep {
  return { step, status: 'failed', at: isoAgo(ago), error: { code, detail } };
}

/** The three-step success plan, in the order the façade runs it. */
export function successSteps(
  oltId: string,
  serial: string,
  ponPort: string,
  lineProfile: string,
  serviceProfile: string,
  vlan: number | null,
  ago: number,
): ActivationStep[] {
  return [
    okStep('authorize', ago, {
      sn: serial,
      olt_id: oltId,
      pon_port: ponPort,
      auth_state: 'authorized',
      status: 'registered',
    }),
    okStep('assign-profile', ago - 1, {
      sn: serial,
      olt_id: oltId,
      line_profile: lineProfile,
      service_profile: serviceProfile,
      vlan,
    }),
    okStep('push-tr069', ago - 2, {
      sn: serial,
      olt_id: oltId,
      tr069: { acs_url: CANNED_CWMP_URL, inform_interval: CANNED_INFORM_INTERVAL },
    }),
  ];
}

/**
 * A preseeded activation: the same document `GET /ops/v1/activations/{id}`
 * answers, plus the request it was made with (the console shows what the
 * operator typed next to what the store derived).
 */
export interface FixtureActivation extends ActivationStatus {
  serial: string;
  olt_id: string;
  pon_port: string;
  requested_at: string;
}

const PROVISIONED_SERIAL = 'ZTEG612004C';
const FAILED_SERIAL = 'CDAT440218';

export const FIXTURE_ACTIVATIONS: readonly FixtureActivation[] = [
  {
    activation_id: 'job:act0b7d31c9e4f2',
    status: 'provisioned',
    serial: PROVISIONED_SERIAL,
    olt_id: 'olt-tehran-02',
    pon_port: 'PON-0/3',
    acs_device_id: `acs-device:${PROVISIONED_SERIAL}`,
    requested_at: isoAgo(96),
    steps: successSteps(
      'olt-tehran-02',
      PROVISIONED_SERIAL,
      'PON-0/3',
      DEFAULT_LINE_PROFILE,
      DEFAULT_SERVICE_PROFILE,
      35,
      96,
    ),
  },
  {
    activation_id: 'job:act2f6a90c14de7',
    status: 'bound',
    serial: 'VSL708311',
    olt_id: 'olt-karaj-01',
    pon_port: 'PON-0/2',
    acs_device_id: 'acs-device:VSL708311',
    requested_at: isoAgo(42),
    steps: successSteps(
      'olt-karaj-01',
      'VSL708311',
      'PON-0/2',
      'LP-GPON-25',
      DEFAULT_SERVICE_PROFILE,
      null,
      42,
    ),
  },
  {
    activation_id: 'job:act5c1e22b8a904',
    status: 'informed',
    serial: 'FHA915520A',
    olt_id: 'olt-qom-01',
    pon_port: 'PON-0/1',
    acs_device_id: 'acs-device:FHA915520A',
    requested_at: isoAgo(18),
    steps: successSteps(
      'olt-qom-01',
      'FHA915520A',
      'PON-0/1',
      DEFAULT_LINE_PROFILE,
      'SP-INET-300M',
      20,
      18,
    ),
  },
  {
    activation_id: 'job:act7d34f0a26be1',
    status: 'await_inform',
    serial: 'HWTC330019B',
    olt_id: 'olt-isfahan-02',
    pon_port: 'PON-0/4',
    requested_at: isoAgo(4),
    steps: successSteps(
      'olt-isfahan-02',
      'HWTC330019B',
      'PON-0/4',
      DEFAULT_LINE_PROFILE,
      DEFAULT_SERVICE_PROFILE,
      12,
      4,
    ),
  },
  {
    // Failed is terminal: the adapter step recorded the error and the job was
    // marked `failed` before the problem response went out (routers/ops.py).
    activation_id: 'job:act9e51b7c3d084',
    status: 'failed',
    serial: FAILED_SERIAL,
    olt_id: 'olt-mashhad-01',
    pon_port: 'PON-0/5',
    requested_at: isoAgo(30),
    steps: [
      okStep('authorize', 30, {
        sn: FAILED_SERIAL,
        olt_id: 'olt-mashhad-01',
        pon_port: 'PON-0/5',
        auth_state: 'authorized',
        status: 'registered',
      }),
      failedStep('assign-profile', 29, 'onu-not-found', 'no ONU CDAT440218 on olt olt-mashhad-01'),
    ],
    error: {
      step: 'assign-profile',
      code: 'onu-not-found',
      detail: 'no ONU CDAT440218 on olt olt-mashhad-01',
    },
  },
];

/** The id the demo/tests start from: a fully provisioned activation. */
export const PROVISIONED_ACTIVATION_ID = 'job:act0b7d31c9e4f2';

/** The id whose read-back carries an `error` block (`status: "failed"`). */
export const FAILED_ACTIVATION_ID = 'job:act9e51b7c3d084';

export function findFixtureActivation(activationId: string): FixtureActivation | undefined {
  return FIXTURE_ACTIVATIONS.find((entry) => entry.activation_id === activationId);
}

/** Status order the façade derives along (`failed` is off this ladder). */
export const ACTIVATION_ADVANCEMENT: readonly ActivationStatusValue[] = [
  'await_inform',
  'informed',
  'bound',
  'provisioned',
];

/**
 * The derivation rule of `get_activation`: a discovered `acs-device:<serial>`
 * raises the activation to `informed`. Never downgrades an advanced status, and
 * never touches a terminal `failed`.
 */
export function raiseToInformed(
  status: ActivationStatusValue,
  deviceDiscovered: boolean,
): ActivationStatusValue {
  if (status === 'failed') return 'failed';
  if (!deviceDiscovered) return status;
  const rank = ACTIVATION_ADVANCEMENT.indexOf(status);
  return rank < 1 ? 'informed' : status;
}
